import callClientPortal from './callClientPortal';
import { isAuthed } from './getJWT';
import languagesMap from './languages.json';
import { AUDIO_JIT_CACHE_BASE_URL } from './audioJitEnv';
import {
  joinAudioJitCacheObjectUrl,
  normalizeAudioJitCacheBaseUrl,
  resolveJitAudioCacheKey
} from './audioJitCacheKey';
import { clearLexiconCaches } from './audioJitLexicon';
import normalizeGoogleNarration from './normalizeGoogleNarration';
import { trackJitSynthesis } from './jitSynthesisInFlight';
import { trackJitSynthesisActive } from './jitSynthesisActive';
import { jitAudioLog } from './jitAudioLog';
import { get, getCourseId, getFirm } from './SCORM';

export type TTSProviderId = 'openai' | 'google' | 'azure';

export interface VoiceOption {
  id: string;
  name: string;
  locale?: string;
  gender?: string;
}

export type JitLanguageRow = {
  voiceLocale?: string;
  audioVoice?: Partial<Record<TTSProviderId, string | null>>;
  audioLocale?: Partial<Record<TTSProviderId, string>>;
};

export interface JitResolvedConfig {
  provider: TTSProviderId;
  model: string;
  voice: string;
  /** Course language key (e.g. en-GB, ar) — used in cache key. */
  locale: string;
  /** BCP-47 tag for provider TTS APIs. */
  voiceLocale: string;
}

export interface JitConfigOverrides {
  provider?: TTSProviderId;
  model?: string;
  voice?: string;
  voiceLocale?: string;
  locale?: string;
  /** Allow falling back google→azure (never openai) when the resolved provider has no voice for the locale. */
  allowProviderFallback?: boolean;
}

/** @deprecated Use {@link JitResolvedConfig} */
export type TTSProviderConfig = JitResolvedConfig;

export interface CacheSettings {
  allowLocalRuntime: boolean;
  allowRead: boolean;
  allowWrite: boolean;
}

export interface JITRequest {
  text: string;
  providerConfig: JitResolvedConfig;
  cacheSettings: CacheSettings;
  cacheBaseUrl?: string;
}

export interface JITResult {
  audioSrc: string;
  cacheKey: string;
  cacheUrl: string;
  latencyMs: number;
  responseTimeMs: number;
  costUsd: number;
  chars: number;
  source: 'cache' | 'provider';
}

export interface TTSProvider {
  id: TTSProviderId;
  label: string;
  defaultModels: string[];
  supportsVoiceList: boolean;
  fetchVoices(config: JitResolvedConfig): Promise<VoiceOption[]>;
}

export const DEFAULT_JIT_PROVIDER_ID: TTSProviderId = 'google';
const languages = languagesMap as Record<string, JitLanguageRow>;

/**
 * Provider fallback order when a locale lacks a voice for the requested provider.
 * openai is excluded deliberately (generic 'alloy' voice only — not used for narration).
 */
const JIT_FALLBACK_PROVIDER_ORDER: TTSProviderId[] = ['google', 'azure'];

const AUTH_JIT_ROUTE = '/audio/ai/jit';
const AUTH_JIT_VOICES_ROUTE = '/audio/ai/jit/voices';
const PUBLIC_JIT_ROUTE = '/public/audio/ai/jit';

const getContentApiV1Base = (): string => {
  const win = window as typeof window & { VITE_API_BASE?: string };
  const defaultOrigin =
    win.VITE_API_BASE ||
    import.meta.env.VITE_API_BASE ||
    window.location.origin.replace(
      /^(https?:\/\/)(\w+)(\..*vinciworks.*\.com)$/,
      '$1portal$3'
    );
  return `${defaultOrigin.replace(/\/$/, '')}/api/content/v1`;
};

const normalizeApiBase = (base: string): string => base.replace(/\/+$/, '');

const nonEmpty = (value: unknown): value is string =>
  typeof value === 'string' && value.trim() !== '';

type JitClientEnvelope = {
  data?: { results?: Record<string, unknown> };
  status?: string;
  message?: string;
  error?: string | unknown[];
};

export const resolveJitConfig = (
  overrides: JitConfigOverrides = {}
): JitResolvedConfig => {
  const courseLocale = overrides.locale?.trim() || 'en-GB';
  const requestedProvider = (overrides.provider ||
    DEFAULT_JIT_PROVIDER_ID) as TTSProviderId;
  const row = languages[courseLocale];

  if (!row) {
    throw new Error(`Unknown course language: ${courseLocale}`);
  }

  const explicitVoice = nonEmpty(overrides.voice) ? overrides.voice.trim() : '';

  let providerId = requestedProvider;
  let voice = explicitVoice || row.audioVoice?.[providerId] || '';

  // When no explicit voice was given and the requested provider has none for this
  // locale, fall back through other real providers (google→azure, never openai).
  if (!explicitVoice && !nonEmpty(voice) && overrides.allowProviderFallback) {
    for (const candidate of JIT_FALLBACK_PROVIDER_ORDER) {
      if (candidate === providerId) continue;
      const candidateVoice = row.audioVoice?.[candidate];
      if (nonEmpty(candidateVoice)) {
        providerId = candidate;
        voice = candidateVoice;
        break;
      }
    }
  }

  if (!nonEmpty(voice)) {
    throw new Error(
      `No default voice for provider "${requestedProvider}" and language "${courseLocale}"` +
        (overrides.allowProviderFallback
          ? ' (and no non-openai fallback voice).'
          : '.')
    );
  }

  const providerChanged = providerId !== requestedProvider;

  const voiceLocale =
    (nonEmpty(overrides.voiceLocale) && overrides.voiceLocale.trim()) ||
    row.audioLocale?.[providerId] ||
    row.voiceLocale ||
    '';

  if (!nonEmpty(voiceLocale)) {
    throw new Error(
      `No voice locale for provider "${providerId}" and language "${courseLocale}".`
    );
  }

  const provider = getProvider(providerId);
  // A model override targets the originally requested provider; if we fell back to
  // a different provider, use that provider's default model instead.
  const model =
    (!providerChanged && nonEmpty(overrides.model) && overrides.model.trim()) ||
    provider.defaultModels[0] ||
    '';

  if (!nonEmpty(model)) {
    throw new Error(`No default model for provider "${providerId}".`);
  }

  return {
    provider: providerId,
    model,
    voice,
    locale: courseLocale,
    voiceLocale
  };
};

async function postJitSynthesis(formData: FormData) {
  if (isAuthed()) {
    return callClientPortal(AUTH_JIT_ROUTE, {
      method: 'POST',
      body: formData
    });
  }

  const firm = getFirm();
  const courseId = getCourseId();
  if (!firm || firm === '___') {
    throw new Error(
      'Audio JIT in published mode requires a firm (tenant) in the URL. Launch from the player or add ?firm=…'
    );
  }

  const audioDataStr = formData.get('audioData');
  if (typeof audioDataStr !== 'string') {
    throw new Error('Invalid audio JIT form data.');
  }

  const parsed = JSON.parse(audioDataStr) as Record<string, unknown>;
  const payload = {
    ...parsed,
    courseId,
    firm,
    locale:
      (typeof parsed.locale === 'string' && parsed.locale.trim()) ||
      get('locale') ||
      'en-GB'
  };
  const fd = new FormData();
  fd.append('audioData', JSON.stringify(payload));

  const envBase = import.meta.env.VITE_PUBLIC_AUDIO_JIT_BASE_URL as
    | string
    | undefined;
  const base = normalizeApiBase(
    (envBase && envBase.trim()) || getContentApiV1Base()
  );
  const url = `${base}${PUBLIC_JIT_ROUTE}`;

  const res = await fetch(url, { method: 'POST', body: fd });
  let envelope: JitClientEnvelope | null = null;
  try {
    envelope = (await res.json()) as JitClientEnvelope;
  } catch {
    envelope = null;
  }

  const errPayload = envelope?.error;
  const hasError =
    errPayload &&
    (Array.isArray(errPayload) ? errPayload.length > 0 : Boolean(errPayload));

  if (!res.ok || hasError) {
    const errText = typeof envelope?.error === 'string' ? envelope.error : '';
    const msg =
      errText ||
      (envelope?.message as string) ||
      (typeof res.statusText === 'string' ? res.statusText : '') ||
      'Public JIT request failed';
    throw new Error(msg);
  }

  return {
    data: envelope as JitClientEnvelope,
    headers: new Headers(),
    response: { status: res.status }
  } as Awaited<ReturnType<typeof callClientPortal>>;
}

export const getAudioJitCacheBaseUrl = (): string =>
  normalizeAudioJitCacheBaseUrl(AUDIO_JIT_CACHE_BASE_URL);

const parseVoicesFromAny = (data: any): VoiceOption[] => {
  if (!data) {
    return [];
  }

  if (Array.isArray(data)) {
    return data
      .map(item => {
        if (typeof item === 'string') {
          return { id: item, name: item };
        }
        return {
          id: item.id || item.voice_id || item.ShortName || item.name,
          name:
            item.name ||
            item.DisplayName ||
            item.ShortName ||
            item.voice_id ||
            item.id,
          locale:
            item.locale || item.Locale || item.language || item.languageCode,
          gender: item.gender || item.Gender || item.ssmlGender
        };
      })
      .filter(item => !!item.id);
  }

  if (Array.isArray(data.voices)) {
    return parseVoicesFromAny(data.voices);
  }

  if (data.data?.results) {
    return parseVoicesFromAny(data.data.results);
  }

  return [];
};

const base64ToBuffer = (base64: string): ArrayBuffer => {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

abstract class BaseProvider implements TTSProvider {
  id: TTSProviderId;
  label: string;
  defaultModels: string[];
  supportsVoiceList: boolean;

  constructor(options: {
    id: TTSProviderId;
    label: string;
    defaultModels: string[];
    supportsVoiceList: boolean;
  }) {
    this.id = options.id;
    this.label = options.label;
    this.defaultModels = options.defaultModels;
    this.supportsVoiceList = options.supportsVoiceList;
  }

  async fetchVoices(config: JitResolvedConfig): Promise<VoiceOption[]> {
    const body = new FormData();
    body.append(
      'audioData',
      JSON.stringify({
        provider: this.id,
        locale: config.locale,
        voiceLocale: config.voiceLocale
      })
    );

    const response = await callClientPortal(AUTH_JIT_VOICES_ROUTE, {
      method: 'POST',
      body
    });
    const results = response?.data?.data?.results;
    return parseVoicesFromAny(results);
  }
}

class OpenAIProvider extends BaseProvider {
  constructor() {
    super({
      id: 'openai',
      label: 'OpenAI',
      defaultModels: ['gpt-4o-mini-tts', 'tts-1-hd', 'tts-1'],
      supportsVoiceList: false
    });
  }
}

class GoogleProvider extends BaseProvider {
  constructor() {
    super({
      id: 'google',
      label: 'Google',
      defaultModels: [
        'cloud-tts',
        'gemini-2.5-flash-tts',
        'gemini-3.1-flash-tts-preview'
      ],
      supportsVoiceList: true
    });
  }
}

class AzureProvider extends BaseProvider {
  constructor() {
    super({
      id: 'azure',
      label: 'Microsoft Azure',
      defaultModels: ['neural'],
      supportsVoiceList: true
    });
  }
}

const providerRegistry: Record<TTSProviderId, TTSProvider> = {
  openai: new OpenAIProvider(),
  google: new GoogleProvider(),
  azure: new AzureProvider()
};

export const providerList = Object.values(providerRegistry).map(provider => ({
  id: provider.id,
  label: provider.label
}));

export const getProvider = (providerId: TTSProviderId): TTSProvider => {
  const provider = providerRegistry[providerId];
  if (!provider) {
    throw new Error(`Unknown provider: ${providerId}`);
  }
  return provider;
};

/** @deprecated Use {@link resolveJitConfig} */
export const resolveProviderConfig = (
  config: JitConfigOverrides = {}
): JitResolvedConfig => resolveJitConfig(config);

export interface EnsureJitCacheWarmRequest {
  text: string;
  providerConfig: JitResolvedConfig;
  cacheSettings: Pick<CacheSettings, 'allowRead' | 'allowWrite'>;
  cacheBaseUrl?: string;
  /** Shown in dev console logs, e.g. `prefetch:tabs-1-tab-0` or `playback:screen-id`. */
  logLabel?: string;
}

export interface EnsureJitCacheWarmResult {
  cacheKey: string;
  cacheUrl: string;
  source: 'cache' | 'provider';
  responseTimeMs?: number;
  costUsd?: number;
}

const resolveCacheKey = async (
  config: JitResolvedConfig,
  text: string
): Promise<string> => {
  // Must hash the same terminator-injected text the payload sends, so the
  // server's re-computed key matches the client cacheKey it validates against.
  const normalizedText = normalizeGoogleNarration(
    text,
    config.provider,
    config.voiceLocale
  );
  try {
    return await resolveJitAudioCacheKey({
      provider: config.provider,
      model: config.model,
      voice: config.voice,
      locale: config.locale,
      voiceLocale: config.voiceLocale,
      text: normalizedText
    });
  } catch (_error) {
    return JSON.stringify([
      config.provider,
      config.model,
      config.voice,
      config.locale,
      normalizedText
    ]);
  }
};

const readCacheHit = async (cacheUrl: string): Promise<boolean> => {
  try {
    const cacheResponse = await fetch(cacheUrl, { method: 'GET' });
    return cacheResponse.ok;
  } catch (_error) {
    return false;
  }
};

const LEXICON_CACHE_KEY_MISMATCH = 'cacheKey does not match server computation';

const isLexiconCacheKeyMismatchError = (error: unknown): boolean =>
  error instanceof Error && error.message.includes(LEXICON_CACHE_KEY_MISMATCH);

type JitSynthesisApiResult = {
  source?: 'cache' | 'provider';
  cacheKey?: string;
  cacheUrl?: string;
  responseTimeMs?: number;
  costUsd?: number;
  audioBase64?: string;
  latencyMs?: number;
};

const postJitSynthesisPayload = async (
  config: JitResolvedConfig,
  text: string,
  cacheKey: string,
  cacheSettings: Pick<CacheSettings, 'allowRead' | 'allowWrite'>
): Promise<JitSynthesisApiResult> => {
  const body = new FormData();
  body.append(
    'audioData',
    JSON.stringify({
      provider: config.provider,
      text: normalizeGoogleNarration(text, config.provider, config.voiceLocale),
      model: config.model,
      voice: config.voice,
      locale: config.locale,
      voiceLocale: config.voiceLocale,
      cacheKey,
      cache: {
        allowWrite: cacheSettings.allowWrite !== false,
        allowRead: cacheSettings.allowRead !== false
      }
    })
  );

  const response = await postJitSynthesis(body);
  return (response?.data?.data?.results || {}) as JitSynthesisApiResult;
};

const synthesizeWithLexiconRetry = async (
  config: JitResolvedConfig,
  text: string,
  cacheSettings: Pick<CacheSettings, 'allowRead' | 'allowWrite'>,
  allowRetry: boolean
): Promise<{ cacheKey: string; result: JitSynthesisApiResult }> => {
  const cacheKey = await resolveCacheKey(config, text);

  try {
    const result = await postJitSynthesisPayload(
      config,
      text,
      cacheKey,
      cacheSettings
    );
    return { cacheKey, result };
  } catch (error) {
    if (!allowRetry || !isLexiconCacheKeyMismatchError(error)) {
      throw error;
    }
    clearLexiconCaches();
    const retriedCacheKey = await resolveCacheKey(config, text);
    const result = await postJitSynthesisPayload(
      config,
      text,
      retriedCacheKey,
      cacheSettings
    );
    return { cacheKey: retriedCacheKey, result };
  }
};

/**
 * Ensures JiT audio exists in the S3 cache without creating playback blob URLs.
 * Deduped via {@link trackJitSynthesis} when a stable cache key is available.
 */
export const ensureJitCacheWarm = async (
  request: EnsureJitCacheWarmRequest
): Promise<EnsureJitCacheWarmResult> => {
  const config = request.providerConfig;
  const cacheBaseUrl =
    normalizeAudioJitCacheBaseUrl(request.cacheBaseUrl) ||
    getAudioJitCacheBaseUrl();
  const label = request.logLabel ?? 'warm';

  const runWarm = async (): Promise<EnsureJitCacheWarmResult> => {
    const attempt = async (
      allowLexiconRetry: boolean
    ): Promise<EnsureJitCacheWarmResult> => {
      const cacheKey = await resolveCacheKey(config, request.text);
      const cacheUrl = joinAudioJitCacheObjectUrl(cacheBaseUrl, cacheKey);

      if (
        request.cacheSettings.allowRead !== false &&
        cacheBaseUrl &&
        cacheKey
      ) {
        jitAudioLog(`${label}: checking S3 cache`);
        if (await readCacheHit(cacheUrl)) {
          jitAudioLog(`${label}: cache hit (no TTS)`);
          return { cacheKey, cacheUrl, source: 'cache' };
        }
      }

      jitAudioLog(`${label}: generating via TTS API`);
      const { cacheKey: synthesisCacheKey, result } =
        await trackJitSynthesisActive(() =>
          synthesizeWithLexiconRetry(
            config,
            request.text,
            request.cacheSettings,
            allowLexiconRetry
          )
        );

      if (!result.audioBase64 && result.source !== 'cache') {
        throw new Error('Backend JIT response did not include audio data.');
      }

      return {
        cacheKey: result.cacheKey || synthesisCacheKey,
        cacheUrl: result.cacheUrl || cacheUrl,
        source: result.source || 'provider',
        responseTimeMs: Number(result.responseTimeMs || 0),
        costUsd: Number(result.costUsd || 0)
      };
    };

    return attempt(true);
  };

  const dedupeKey = await resolveCacheKey(config, request.text);
  if (dedupeKey) {
    return trackJitSynthesis(dedupeKey, runWarm);
  }

  return runWarm();
};

export const runJITSynthesis = async (
  request: JITRequest
): Promise<JITResult> => {
  const config = request.providerConfig;
  const startedAt = performance.now();
  const chars = request.text.length;

  const warm = await ensureJitCacheWarm({
    text: request.text,
    providerConfig: config,
    cacheSettings: {
      allowRead: request.cacheSettings.allowRead,
      allowWrite: request.cacheSettings.allowWrite
    },
    cacheBaseUrl: request.cacheBaseUrl,
    logLabel: 'playback'
  });

  const cacheUrl =
    warm.cacheUrl ||
    joinAudioJitCacheObjectUrl(
      normalizeAudioJitCacheBaseUrl(request.cacheBaseUrl) ||
        getAudioJitCacheBaseUrl(),
      warm.cacheKey
    );

  try {
    jitAudioLog('playback: fetching blob for play');
    const cacheResponse = await fetch(cacheUrl, { method: 'GET' });
    if (cacheResponse.ok) {
      const audioBlob = await cacheResponse.blob();
      const audioSrc = URL.createObjectURL(audioBlob);
      jitAudioLog('playback: ready', {
        source: warm.source,
        key: warm.cacheKey
      });

      return {
        audioSrc,
        cacheKey: warm.cacheKey,
        cacheUrl,
        source: warm.source,
        latencyMs: performance.now() - startedAt,
        responseTimeMs: warm.responseTimeMs ?? 0,
        costUsd: warm.costUsd ?? 0,
        chars
      };
    }
  } catch (_error) {
    // fall through to direct synthesis response path below
  }

  const body = new FormData();
  body.append(
    'audioData',
    JSON.stringify({
      provider: config.provider,
      text: request.text,
      model: config.model,
      voice: config.voice,
      locale: config.locale,
      voiceLocale: config.voiceLocale,
      cacheKey: warm.cacheKey,
      cache: {
        allowWrite: request.cacheSettings.allowWrite,
        allowRead: request.cacheSettings.allowRead
      }
    })
  );

  let playbackCacheKey = warm.cacheKey;
  let result: JitSynthesisApiResult;
  try {
    result = await postJitSynthesisPayload(
      config,
      request.text,
      warm.cacheKey,
      request.cacheSettings
    );
  } catch (error) {
    if (!isLexiconCacheKeyMismatchError(error)) {
      throw error;
    }
    clearLexiconCaches();
    playbackCacheKey = await resolveCacheKey(config, request.text);
    result = await postJitSynthesisPayload(
      config,
      request.text,
      playbackCacheKey,
      request.cacheSettings
    );
  }

  if (!result.audioBase64) {
    throw new Error('Backend JIT response did not include audio data.');
  }

  const audioBuffer = base64ToBuffer(result.audioBase64);
  const audioBlob = new Blob([audioBuffer], { type: 'audio/mpeg' });
  const audioSrc = URL.createObjectURL(audioBlob);

  return {
    audioSrc,
    cacheKey: result.cacheKey || playbackCacheKey,
    cacheUrl: result.cacheUrl || cacheUrl,
    source: result.source || warm.source,
    latencyMs: Number(result.latencyMs || performance.now() - startedAt),
    responseTimeMs: Number(result.responseTimeMs || warm.responseTimeMs || 0),
    costUsd: Number(result.costUsd || warm.costUsd || 0),
    chars: Number(result.chars || chars)
  };
};

export const parseAudioScriptToEntries = (
  raw: string
): Array<{ filename: string; text: string }> => {
  const parsed = JSON.parse(raw);

  if (!Array.isArray(parsed)) {
    throw new Error(
      'Audio script must be an array of single-key objects, e.g. [{ "screen-1.mp3": "Hello" }].'
    );
  }

  const entries: Array<{ filename: string; text: string }> = [];

  parsed.forEach((item, index) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      throw new Error(
        `Entry ${index + 1} must be an object with exactly one key/value pair.`
      );
    }

    const pairs = Object.entries(item);
    if (pairs.length !== 1) {
      throw new Error(
        `Entry ${index + 1} must contain exactly one key/value pair (filename => text).`
      );
    }

    const [filename, text] = pairs[0];
    if (typeof filename !== 'string' || !filename.trim()) {
      throw new Error(`Entry ${index + 1} has an invalid filename key.`);
    }
    if (typeof text !== 'string') {
      throw new Error(`Entry ${index + 1} value must be a string.`);
    }

    entries.push({
      filename: filename.trim(),
      text
    });
  });

  return entries;
};
